import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { PersonSummary } from '../avatar';
import { usePickerPanelPosition } from '../timePickerModern/usePickerPanelPosition';
import './SearchableSelect.css';

/**
 * @typedef {object} SelectOption
 * @property {string} value
 * @property {string} label
 * @property {import('react').ReactNode} [icon]
 * @property {string} [image]
 * @property {string} [initials]
 * @property {string} [subtitle]
 * @property {string} [meta]
 * @property {'full' | 'sm'} [imageRadius]
 * @property {boolean} [disabled]
 */

/**
 * @typedef {object} SearchableSelectProps
 * @property {SelectOption[]} options
 * @property {string} [value]
 * @property {(value: string) => void} onChange
 * @property {string} [placeholder]
 * @property {string} [searchPlaceholder]
 * @property {string} [emptyMessage]
 * @property {boolean} [disabled]
 * @property {string} [className]
 * @property {number} [maxVisibleOptions]
 * @property {string} [id]
 * @property {string} [name]
 * @property {string} [label]
 * @property {boolean} [clearable]
 * @property {string} [error]
 * @property {boolean} [required]
 */

const OPTION_HEIGHT_REM = 2.5;
const PERSON_OPTION_HEIGHT_REM = 4.25;
const SEARCH_HEIGHT_REM = 2.5;

const normalizeSearchText = (text) =>
    String(text || '')
        .toLowerCase()
        .replace(/\s+/g, '')
        .trim();

/**
 * @param {import('react').ReactNode} [icon]
 * @param {string} [image]
 * @param {string} [initials]
 * @param {'full' | 'sm'} [imageRadius]
 */
const OptionMedia = ({ icon, image, initials, imageRadius = 'full' }) => {
    if (initials) {
        return (
            <span className="searchable-select__avatar" aria-hidden="true">
                <span className="searchable-select__avatar-text">{initials}</span>
            </span>
        );
    }

    if (image) {
        return (
            <span className="searchable-select__media" aria-hidden="true">
                <img
                    src={image}
                    alt=""
                    className={
                        imageRadius === 'sm'
                            ? 'searchable-select__image searchable-select__image--sm'
                            : 'searchable-select__image searchable-select__image--full'
                    }
                />
            </span>
        );
    }

    if (icon) {
        return (
            <span className="searchable-select__media" aria-hidden="true">
                {icon}
            </span>
        );
    }

    return null;
};

const isPersonOption = (option) => Boolean(
    option?.initials && (option?.subtitle || option?.meta),
);

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.initials
 * @param {string} [props.subtitle]
 * @param {string} [props.meta]
 */
const PersonOptionContent = ({ label, initials, subtitle, meta }) => (
    <PersonSummary
        className="searchable-select__person-option"
        initials={initials}
        name={label}
        role={subtitle}
        meta={meta}
        avatarSize="md"
    />
);

/**
 * @param {SearchableSelectProps} props
 */
export const SearchableSelect = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Seleccionar opción',
    searchPlaceholder = 'Buscar...',
    emptyMessage = 'No se encontraron resultados',
    disabled = false,
    className = '',
    maxVisibleOptions = 4,
    id,
    name,
    label,
    clearable = false,
    error = null,
    required = false,
}) => {
    const generatedId = useId();
    const comboboxId = id || `searchable-select-${generatedId}`;
    const listboxId = `${comboboxId}-listbox`;
    const searchId = `${comboboxId}-search`;

    const containerRef = useRef(null);
    const triggerWrapRef = useRef(null);
    const panelRef = useRef(null);
    const searchInputRef = useRef(null);
    const optionsRef = useRef(null);
    const optionRefs = useRef([]);

    const [isOpen, setIsOpen] = useState(false);
    const { placement, compact, position: panelPosition } = usePickerPanelPosition(
        triggerWrapRef,
        panelRef,
        isOpen,
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const selectedOption = useMemo(
        () => options.find((option) => option.value === value) ?? null,
        [options, value],
    );

    const filteredOptions = useMemo(() => {
        const query = normalizeSearchText(searchQuery);
        if (!query) return options;

        return options.filter((option) => {
            const labelText = typeof option.label === 'string' ? option.label : '';
            const haystack = normalizeSearchText(
                `${labelText}${option.value || ''}${option.subtitle || ''}${option.meta || ''}`,
            );
            return haystack.includes(query);
        });
    }, [options, searchQuery]);

    const hasPersonOptions = useMemo(
        () => options.some((option) => isPersonOption(option)),
        [options],
    );

    const optionHeightRem = hasPersonOptions ? PERSON_OPTION_HEIGHT_REM : OPTION_HEIGHT_REM;
    const optionsMaxHeight = `calc(${maxVisibleOptions} * ${optionHeightRem}rem + 0.5rem)`;

    const findHighlightIndex = useCallback((direction, fromIndex = highlightedIndex) => {
        if (filteredOptions.length === 0) return -1;

        const step = direction === 'down' ? 1 : -1;
        let index = fromIndex;

        if (index < 0) {
            index = direction === 'down' ? 0 : filteredOptions.length - 1;
        } else {
            index += step;
        }

        for (let attempts = 0; attempts < filteredOptions.length; attempts += 1) {
            if (index < 0) index = filteredOptions.length - 1;
            if (index >= filteredOptions.length) index = 0;

            if (!filteredOptions[index]?.disabled) {
                return index;
            }

            index += step;
        }

        return -1;
    }, [filteredOptions, highlightedIndex]);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
    }, []);

    const openDropdown = useCallback(() => {
        if (disabled) return;
        setIsOpen(true);
        setHighlightedIndex(-1);
    }, [disabled]);

    const selectOption = useCallback(
        (option) => {
            if (!option || option.disabled) return;
            onChange(option.value);
            closeDropdown();
        },
        [closeDropdown, onChange],
    );

    const handleClear = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange('');
        closeDropdown();
    }, [closeDropdown, onChange]);

    const hasSelection = Boolean(value);
    const showClear = clearable && hasSelection && !disabled;
    const isPersonSelected = selectedOption ? isPersonOption(selectedOption) : false;

    const scrollHighlightedIntoView = useCallback((index) => {
        const node = optionRefs.current[index];
        if (node) {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;

        const frame = requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });

        return () => cancelAnimationFrame(frame);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (
                containerRef.current?.contains(event.target)
                || panelRef.current?.contains(event.target)
            ) {
                return;
            }
            closeDropdown();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [closeDropdown, isOpen]);

    useEffect(() => {
        if (highlightedIndex < 0) return;
        scrollHighlightedIntoView(highlightedIndex);
    }, [highlightedIndex, scrollHighlightedIntoView]);

    const handleTriggerKeyDown = (event) => {
        if (disabled) return;

        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
            case 'Enter':
            case ' ':
                event.preventDefault();
                openDropdown();
                if (event.key === 'ArrowDown') {
                    setHighlightedIndex(findHighlightIndex('down', -1));
                }
                if (event.key === 'ArrowUp') {
                    setHighlightedIndex(findHighlightIndex('up', filteredOptions.length));
                }
                break;
            case 'Escape':
                event.preventDefault();
                closeDropdown();
                break;
            default:
                break;
        }
    };

    const handleSearchKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                setHighlightedIndex((prev) => findHighlightIndex('down', prev));
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();
                setHighlightedIndex((prev) => findHighlightIndex('up', prev));
                break;
            }
            case 'Enter': {
                event.preventDefault();
                const activeOption = filteredOptions[highlightedIndex];
                if (activeOption && !activeOption.disabled) {
                    selectOption(activeOption);
                }
                break;
            }
            case 'Escape':
                event.preventDefault();
                closeDropdown();
                break;
            case 'Tab':
                closeDropdown();
                break;
            default:
                break;
        }
    };

    const activeDescendantId =
        highlightedIndex >= 0 && filteredOptions[highlightedIndex]
            ? `${comboboxId}-option-${filteredOptions[highlightedIndex].value}`
            : undefined;

    const isPanelVisible = isOpen && Boolean(panelPosition);
    const panelNode = (
        <div
            ref={panelRef}
            className="searchable-select__panel searchable-select__panel--portal"
            data-open={isPanelVisible ? 'true' : 'false'}
            data-placement={placement}
            data-compact={compact}
            style={{
                position: 'fixed',
                zIndex: 99999,
                visibility: isPanelVisible ? 'visible' : 'hidden',
                pointerEvents: isPanelVisible ? 'auto' : 'none',
                left: panelPosition?.left,
                width: panelPosition?.width,
                maxHeight: panelPosition?.maxHeight,
                ...(panelPosition?.top != null
                    ? { top: panelPosition.top, bottom: 'auto' }
                    : { bottom: panelPosition?.bottom, top: 'auto' }),
            }}
        >
            <div className="searchable-select__search-wrap">
                <input
                    ref={searchInputRef}
                    id={searchId}
                    type="search"
                    className="searchable-select__search"
                    role="searchbox"
                    aria-controls={listboxId}
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    onChange={(event) => {
                        setSearchQuery(event.target.value);
                        setHighlightedIndex(-1);
                    }}
                    onKeyDown={handleSearchKeyDown}
                />
            </div>

            <div
                ref={optionsRef}
                id={listboxId}
                role="listbox"
                aria-labelledby={label ? `${comboboxId}-label` : comboboxId}
                className="searchable-select__options"
                style={{ maxHeight: optionsMaxHeight }}
            >
                {filteredOptions.length === 0 ? (
                    <div className="searchable-select__empty" role="status">
                        {emptyMessage}
                    </div>
                ) : (
                    filteredOptions.map((option, index) => {
                        const isSelected = option.value === value;
                        const isActive = index === highlightedIndex;

                        return (
                            <button
                                key={option.value}
                                ref={(node) => {
                                    optionRefs.current[index] = node;
                                }}
                                id={`${comboboxId}-option-${option.value}`}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                disabled={option.disabled}
                                data-selected={isSelected}
                                data-active={isActive}
                                className={`searchable-select__option${
                                    isPersonOption(option) ? ' searchable-select__option--person' : ''
                                }`}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onClick={() => selectOption(option)}
                            >
                                {isPersonOption(option) ? (
                                    <PersonOptionContent
                                        label={option.label}
                                        initials={option.initials}
                                        subtitle={option.subtitle}
                                        meta={option.meta}
                                    />
                                ) : (
                                    <>
                                        <OptionMedia
                                            icon={option.icon}
                                            image={option.image}
                                            initials={option.initials}
                                            imageRadius={option.imageRadius}
                                        />
                                        <span className="searchable-select__option-label">
                                            {option.label}
                                        </span>
                                    </>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={`searchable-select ${className}`.trim()}
        >
            {label ? (
                <label htmlFor={comboboxId} id={`${comboboxId}-label`} className="searchable-select__label">
                    {label}
                    {required ? (
                        <span className="searchable-select__required" aria-hidden="true">*</span>
                    ) : null}
                </label>
            ) : null}

            <input type="hidden" name={name} value={value || ''} readOnly />

            <div
                ref={triggerWrapRef}
                className="searchable-select__trigger-wrap"
                data-open={isOpen ? 'true' : 'false'}
                data-error={error ? 'true' : 'false'}
            >
                <button
                    type="button"
                    id={comboboxId}
                    className={`searchable-select__trigger${
                        isPersonSelected ? ' searchable-select__trigger--person' : ''
                    }`}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-activedescendant={isOpen ? activeDescendantId : undefined}
                    aria-disabled={disabled || undefined}
                    aria-labelledby={label ? `${comboboxId}-label` : undefined}
                    aria-label={label ? undefined : placeholder}
                    data-open={isOpen ? 'true' : 'false'}
                    data-has-clear={showClear}
                    disabled={disabled}
                    onClick={() => {
                        if (isOpen) {
                            closeDropdown();
                        } else {
                            openDropdown();
                        }
                    }}
                    onKeyDown={handleTriggerKeyDown}
                >
                    {selectedOption ? (
                        isPersonSelected ? (
                            <PersonOptionContent
                                label={selectedOption.label}
                                initials={selectedOption.initials}
                                subtitle={selectedOption.subtitle}
                                meta={selectedOption.meta}
                            />
                        ) : (
                            <>
                                <OptionMedia
                                    icon={selectedOption.icon}
                                    image={selectedOption.image}
                                    initials={selectedOption.initials}
                                    imageRadius={selectedOption.imageRadius}
                                />
                                <span className="searchable-select__trigger-label">
                                    {selectedOption.label}
                                </span>
                            </>
                        )
                    ) : (
                        <span className="searchable-select__trigger-label searchable-select__trigger-placeholder">
                            {placeholder}
                        </span>
                    )}
                </button>

                <div className="searchable-select__actions">
                    {showClear ? (
                        <button
                            type="button"
                            className="searchable-select__clear"
                            aria-label="Quitar selección"
                            onClick={handleClear}
                        >
                            <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    ) : null}
                    <ChevronDownIcon className="searchable-select__chevron" aria-hidden="true" />
                </div>
            </div>

            {isOpen && typeof document !== 'undefined'
                ? createPortal(panelNode, document.body)
                : null}

            {error ? (
                <p className="searchable-select__error" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};
